import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import Landing from './pages/Landing';
import Join from './pages/Join';
import NotFound from './pages/NotFound';

/**
 * Routing.
 *
 * Landing and Join load eagerly — they're the capital-call email's destinations
 * and must open instantly from a WhatsApp link.
 *
 * Everything else is lazy. That's not just tidiness: /admin pulls in Firebase
 * Auth and Firestore, which would otherwise land in the landing-page chunk and
 * cost the Lighthouse budget for no reason.
 */
const History = lazy(() => import('./pages/History'));
const Welcome = lazy(() => import('./pages/Welcome'));
const League = lazy(() => import('./pages/League'));
const Prizes = lazy(() => import('./pages/Prizes'));
const Admin = lazy(() => import('./pages/admin/Admin'));

/**
 * Start every new page at the top.
 *
 * React Router keeps the window's scroll position across route changes, so
 * following "See the full breakdown" from halfway down the landing page opened
 * /prizes already halfway down — past its own headline.
 *
 * POP is deliberately excluded. That is the back and forward buttons, where the
 * browser restores the position you left, and clobbering it would be a worse
 * bug than the one this fixes.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === 'POP') return;

    window.scrollTo(0, 0);

    /*
     * And again on the next frame.
     *
     * The first call lands before the new page has finished settling — a lazy
     * chunk resolving, the web font swapping in, an image taking its height.
     * If the document grows after that call, the browser can restore the old
     * offset and put you back where you were, which looks exactly like the
     * reset never ran.
     */
    const frame = requestAnimationFrame(() => window.scrollTo(0, 0));
    return () => cancelAnimationFrame(frame);
  }, [pathname, navigationType]);

  return null;
}

function RouteFallback() {
  return (
    <div className="wrap section" role="status" aria-live="polite">
      <p className="muted">Loading…</p>
    </div>
  );
}

export default function App() {
  return (
    <>
      {/* Outside Suspense: the reset should happen as the route changes, not
          after a lazy chunk has finished downloading. */}
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/join" element={<Join />} />
          <Route path="/history" element={<History />} />
          <Route path="/welcome/:slug" element={<Welcome />} />
          <Route path="/league" element={<League />} />
          <Route path="/prizes" element={<Prizes />} />
          <Route path="/admin/*" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
